/**
 * Available theme names
 */
export type ThemeName = 'elkaBlue' | 'arabGolden';

/**
 * Theme color definitions
 *
 * NOTE (calm-v2): the gradient/glow/per-section accent fields below
 * (headerGlow*, titleGradient, pageTitleGradient, nav*, cardBorder*, cardIcon*)
 * are deprecated for elka-manager and crm — calm-v2 screens use the flat
 * `calmColors` / `v2/calm.css` layer instead. Fields are kept (not deleted)
 * while legacy screens still read them.
 */
export interface ThemeColors {
  // Header/Footer backgrounds
  headerBg: string;
  headerGlow1: string;
  headerGlow2: string;
  headerGlow3: string;
  headerTextLight: string;
  footerTextLight: string;

  // Primary gradient (logo, buttons)
  primaryGradient: string;
  primaryGradientHover: string;

  // Text colors
  titleGradient: string;
  pageTitleGradient: string;
  subtitleText: string;

  // Navigation colors (app-specific)
  navClients: string;
  navProjects: string;
  navSuppliers: string;
  navOrders: string;
  navSidur: string;
  navTasks: string;
  navSettings: string;
  navToolbox: string;

  // UI Elements
  buttonPrimary: string;
  buttonPrimaryHover: string;
  buttonSecondaryBg: string;
  buttonSecondaryText: string;
  buttonGradient: string;

  // Cards & Borders (app-specific)
  cardBorderClients: string;
  cardBorderProjects: string;
  cardBorderSuppliers: string;
  cardBorderOrders: string;
  cardIconClients: string;
  cardIconProjects: string;
  cardIconSuppliers: string;
  cardIconOrders: string;

  // Status colors
  statusPaid: string;
  statusToPay: string;
  statusInProgress: string;
  statusEstimate: string;
  statusInvoice: string;
  statusOrder: string;
  statusOpen: string;

  // Accents
  focusRing: string;
  todayHighlight: string;
  todayBorder: string;

  // App-specific: Sidur (elka-manager)
  sidurClientHeader: string;
  sidurWorkerPanel: string;
  sidurAssignment: string;
}

/**
 * Theme definitions
 */
export const themes: Record<ThemeName, ThemeColors> = {
  elkaBlue: {
    headerBg: 'from-[#0C1A55] via-[#3C5FA8] to-[#0C1A55]',
    headerGlow1: 'bg-[#3C5FA8]',
    headerGlow2: 'bg-[#0C1A55]',
    headerGlow3: 'bg-[#3C5FA8]',
    headerTextLight: 'text-white',
    footerTextLight: 'text-gray-200',

    primaryGradient: 'from-[#0C1A55] via-[#3C5FA8] to-[#0C1A55]',
    primaryGradientHover: 'from-[#0A1545] via-[#2F4A8A] to-[#0A1545]',

    titleGradient: 'from-blue-200 via-white to-blue-200',
    pageTitleGradient: 'from-[#0C1A55] via-[#3C5FA8] to-[#0C1A55]',
    subtitleText: 'text-blue-200',

    navClients: 'from-[#3C5FA8] to-[#0C1A55]',
    navProjects: 'from-[#0C1A55] to-[#3C5FA8]',
    navSuppliers: 'from-[#3C5FA8] to-[#0C1A55]',
    navOrders: 'from-[#0C1A55] to-[#3C5FA8]',
    navSidur: 'from-[#0C1A55] to-[#3C5FA8]',
    navTasks: 'from-[#3C5FA8] to-[#0C1A55]',
    navSettings: 'from-[#1A1F2C] to-[#0C1A55]',
    navToolbox: 'from-purple-500 to-indigo-600',

    buttonPrimary: 'bg-[#3C5FA8] hover:bg-[#0C1A55] focus:ring-[#3C5FA8]',
    buttonPrimaryHover: 'bg-[#0C1A55]',
    buttonSecondaryBg: 'bg-[#E9F0FF] hover:bg-[#F4F6FA]',
    buttonSecondaryText: 'text-[#0C1A55]',
    buttonGradient: 'from-[#0C1A55] via-[#3C5FA8] to-[#0C1A55]',

    cardBorderClients: 'border-l-[#3C5FA8]',
    cardBorderProjects: 'border-l-[#0C1A55]',
    cardBorderSuppliers: 'border-l-[#3C5FA8]',
    cardBorderOrders: 'border-l-[#3C5FA8]',
    cardIconClients: 'from-[#3C5FA8] to-[#0C1A55]',
    cardIconProjects: 'from-[#0C1A55] to-[#3C5FA8]',
    cardIconSuppliers: 'from-[#3C5FA8] to-[#0C1A55]',
    cardIconOrders: 'from-[#3C5FA8] to-[#0C1A55]',

    statusPaid: 'bg-emerald-100 text-emerald-800 border-emerald-400',
    statusToPay: 'bg-amber-100 text-amber-800 border-amber-400',
    statusInProgress: 'bg-sky-100 text-sky-800 border-sky-400',
    statusEstimate: 'bg-stone-100 text-stone-700 border-stone-400',
    statusInvoice: 'bg-orange-100 text-orange-800 border-orange-400',
    statusOrder: 'bg-indigo-100 text-indigo-800 border-indigo-400',
    statusOpen: 'bg-purple-100 text-purple-800 border-purple-400',

    focusRing: 'focus:ring-[#3C5FA8]',
    todayHighlight: '#E9F0FF',
    todayBorder: '#0C1A55',

    sidurClientHeader: 'from-[#0C1A55] to-[#3C5FA8]',
    sidurWorkerPanel: 'from-[#F4F6FA] to-[#E9F0FF] border-[#3C5FA8]',
    sidurAssignment: 'from-[#E9F0FF] to-[#F4F6FA] border-[#3C5FA8]',
  },
  arabGolden: {
    headerBg: 'from-amber-950 via-yellow-900 to-amber-950',
    headerGlow1: 'bg-amber-500',
    headerGlow2: 'bg-yellow-400',
    headerGlow3: 'bg-orange-500',
    headerTextLight: 'text-white',
    footerTextLight: 'text-gray-200',

    primaryGradient: 'from-amber-500 via-yellow-500 to-orange-500',
    primaryGradientHover: 'from-amber-600 via-yellow-600 to-orange-600',

    titleGradient: 'from-amber-600 via-yellow-600 to-orange-600',
    pageTitleGradient: 'from-amber-600 via-yellow-600 to-orange-600',
    subtitleText: 'text-amber-300',

    navClients: 'from-amber-600 to-yellow-600',
    navProjects: 'from-orange-600 to-red-600',
    navSuppliers: 'from-yellow-600 to-amber-600',
    navOrders: 'from-red-600 to-orange-600',
    navSidur: 'from-amber-700 to-orange-700',
    navTasks: 'from-yellow-600 to-orange-600',
    navSettings: 'from-stone-600 to-amber-700',
    navToolbox: 'from-purple-600 to-pink-600',

    buttonPrimary: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
    buttonPrimaryHover: 'bg-amber-700',
    buttonSecondaryBg: 'bg-amber-100 hover:bg-amber-200',
    buttonSecondaryText: 'text-amber-900',
    buttonGradient: 'from-amber-500 via-yellow-500 to-orange-500',

    cardBorderClients: 'border-l-amber-500',
    cardBorderProjects: 'border-l-orange-500',
    cardBorderSuppliers: 'border-l-yellow-500',
    cardBorderOrders: 'border-l-red-500',
    cardIconClients: 'from-amber-600 to-yellow-600',
    cardIconProjects: 'from-orange-600 to-red-600',
    cardIconSuppliers: 'from-yellow-600 to-amber-600',
    cardIconOrders: 'from-red-600 to-orange-600',

    statusPaid: 'bg-emerald-100 text-emerald-800 border-emerald-400',
    statusToPay: 'bg-amber-100 text-amber-800 border-amber-400',
    statusInProgress: 'bg-sky-100 text-sky-800 border-sky-400',
    statusEstimate: 'bg-stone-100 text-stone-700 border-stone-400',
    statusInvoice: 'bg-orange-100 text-orange-800 border-orange-400',
    statusOrder: 'bg-indigo-100 text-indigo-800 border-indigo-400',
    statusOpen: 'bg-purple-100 text-purple-800 border-purple-400',

    focusRing: 'focus:ring-amber-500',
    todayHighlight: '#fef3c7',
    todayBorder: '#d97706',

    sidurClientHeader: 'from-amber-700 to-orange-700',
    sidurWorkerPanel: 'from-amber-50 to-yellow-50 border-amber-200',
    sidurAssignment: 'from-amber-100 to-yellow-50 border-amber-400',
  },
};
